import { FormControlLabel, MenuItem, Switch, TextField } from '@mui/material';
import { useState } from 'react';

import type {
  CustomizationField,
  CustomizationFieldInput,
  CustomizationFieldType,
} from '../../api/business/types';
import { FormDialog } from '../../components/FormDialog';
import { LocalisedTextField } from '../../components/LocalisedTextField';
import { changedTexts, textsOf } from '../../components/productFiles';
import { useContentLanguages } from '../../components/useContentLanguages';
import { useLanguage } from '../../i18n/LanguageContext';

type CustomizationFieldDialogProps = {
  /** The field edited, or null for a new one. */
  field: CustomizationField | null;
  isBusy: boolean;
  error: unknown;
  onSave: (field: CustomizationFieldInput) => void;
  onClose: () => void;
};

const TYPES: Array<CustomizationFieldType> = ['TEXT', 'FILE'];

/** One customization field (R0030 WP-11): text or file, required or not, its label per content language. */
export const CustomizationFieldDialog = ({
  field,
  isBusy,
  error,
  onSave,
  onClose,
}: CustomizationFieldDialogProps) => {
  const { t } = useLanguage();
  const { languages, available } = useContentLanguages();
  const [type, setType] = useState<CustomizationFieldType>(field?.type ?? 'TEXT');
  const [required, setRequired] = useState(field?.required ?? false);
  const [labels, setLabels] = useState(() => textsOf(field?.labels ?? {}, languages));
  const [problem, setProblem] = useState<string | null>(null);

  const submit = (): void => {
    if ((labels[languages[0]] ?? '').trim() === '') {
      setProblem(t('customization.labelRequired'));

      return;
    }

    setProblem(null);
    onSave({
      ...(field ? { id: field.id } : {}),
      type,
      required,
      labels: changedTexts(labels, field?.labels ?? {}),
    });
  };

  return (
    <FormDialog
      title={field ? t('customization.edit') : t('customization.add')}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('common.save')}
      isBusy={isBusy}
      error={error}
      problem={problem}
    >
      <LocalisedTextField
        label={t('customization.label')}
        languages={languages}
        available={available}
        values={labels}
        onChange={setLabels}
        maxLength={255}
        autoFocus
      />
      <TextField
        select
        label={t('customization.type')}
        value={type}
        onChange={(e) => setType(e.target.value as CustomizationFieldType)}
      >
        {TYPES.map((one) => (
          <MenuItem key={one} value={one}>
            {t(`customization.type.${one}`)}
          </MenuItem>
        ))}
      </TextField>
      <FormControlLabel
        control={<Switch checked={required} onChange={(e) => setRequired(e.target.checked)} />}
        label={t('customization.required')}
      />
    </FormDialog>
  );
};
